import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, Copy, Trash2, HeartPulse, Pencil } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { PassportBody } from "../public/PassportView";

const HEALTH = [["allergies", "Allergies"], ["medications", "Medications & supplements"], ["conditions", "Medical conditions & surgeries"],
  ["blood_type", "Blood type"], ["emergency_contact", "Emergency contact"], ["gp_contact", "Family doctor / GP"]];
const PARTS = [["health", "Health summary"], ["treatments", "Treatment history"], ["notes", "Notes shared by the clinic"], ["consents", "Signed consent forms"], ["photos", "Before & after photos"]];
const TIMES = [[1, "1 hour"], [24, "24 hours"], [168, "7 days"], [720, "30 days"]];

function HealthEditor({ slug, health, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(health || {});
  useEffect(() => { setF(health || {}); }, [health]);
  const save = () => api.put(`/portal/${slug}/health`, f).then(() => { toast.success("Saved"); setOpen(false); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  return (<>
    <Button size="sm" variant="outline" className="rounded-full" data-testid="passport-edit-health" onClick={() => setOpen(true)}><Pencil className="h-4 w-4 mr-1" />Update my health info</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="passport-health-dialog">
        <DialogHeader><DialogTitle>My health information</DialogTitle></DialogHeader>
        {HEALTH.map(([k, label]) => (
          <div key={k} className="space-y-1"><label className="text-xs font-semibold text-muted-foreground">{label}</label>
            {["blood_type", "emergency_contact", "gp_contact"].includes(k) ? <Input data-testid={`health-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
              : <Textarea data-testid={`health-${k}`} rows={2} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}</div>))}
        <Button className="rounded-full" data-testid="health-save" onClick={save}>Save</Button>
      </DialogContent>
    </Dialog>
  </>);
}

function ShareDialog({ slug, open, onOpenChange, onCreated }) {
  const [hours, setHours] = useState(24);
  const [inc, setInc] = useState(["health", "treatments", "notes"]);
  const [label, setLabel] = useState("");
  const [share, setShare] = useState(null);
  useEffect(() => { if (open) setShare(null); }, [open]);
  const create = () => api.post(`/portal/${slug}/shares`, { hours, include: inc, label }).then((r) => { setShare(r.data); onCreated(); }).catch((e) => toast.error(errMsg(e)));
  const url = share && `${window.location.origin}/passport/${share.token}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="passport-share-dialog">
        <DialogHeader><DialogTitle>Share with a doctor</DialogTitle></DialogHeader>
        {share ? (
          <div className="space-y-4 text-center" data-testid="passport-qr">
            <div className="inline-block rounded-xl bg-white p-4 border"><QRCodeSVG value={url} size={220} /></div>
            <p className="text-sm text-muted-foreground">Ask the doctor to scan this code. It expires {new Date(share.expires_at).toLocaleString()}.</p>
            <div className="flex gap-2"><Input readOnly value={url} className="text-xs font-mono" data-testid="passport-share-url" />
              <Button size="icon" variant="outline" data-testid="passport-copy-url" onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">{PARTS.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`share-include-${k}`} checked={inc.includes(k)}
                onChange={(e) => setInc(e.target.checked ? [...inc, k] : inc.filter((x) => x !== k))} />{l}</label>))}</div>
            <div className="flex flex-wrap gap-2">{TIMES.map(([h, l]) => <Button key={h} size="sm" variant={hours === h ? "default" : "outline"} className="rounded-full" data-testid={`share-hours-${h}`} onClick={() => setHours(h)}>{l}</Button>)}</div>
            <Input data-testid="share-label" placeholder="Who is it for? (optional, e.g. Dr Rossi)" value={label} onChange={(e) => setLabel(e.target.value)} />
            <Button className="rounded-full w-full" data-testid="share-create-btn" disabled={!inc.length} onClick={create}><QrCode className="h-4 w-4 mr-1.5" />Create QR code</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PassportTab({ slug }) {
  const [p, setP] = useState(null);
  const [shares, setShares] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    api.get(`/portal/${slug}/passport`).then((r) => setP(r.data));
    api.get(`/portal/${slug}/shares`).then((r) => setShares(r.data));
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;
  return (
    <div className="space-y-5" data-testid="portal-passport">
      <div className="rounded-[var(--radius)] border bg-card p-5 flex flex-wrap items-center gap-3">
        <HeartPulse className="h-6 w-6 text-primary" />
        <div className="flex-1 min-w-[200px]"><div className="font-semibold">Health & Beauty Passport</div><div className="text-xs text-muted-foreground">Your health, treatments and notes in one place. Share them safely with any doctor using a QR code.</div></div>
        <HealthEditor slug={slug} health={p.health} onSaved={load} />
        <Button size="sm" className="rounded-full" data-testid="passport-share-btn" onClick={() => setOpen(true)}><QrCode className="h-4 w-4 mr-1" />Share with a doctor</Button>
      </div>
      {shares.length > 0 && <div className="space-y-2" data-testid="passport-shares">
        <div className="text-xs font-semibold text-muted-foreground">Active shares</div>
        {shares.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3 text-sm" data-testid={`passport-share-${s.id}`}>
            <span className="flex-1">{s.label || "Shared link"} · {s.include.join(", ")}</span>
            <span className="text-xs text-muted-foreground">Expires {new Date(s.expires_at).toLocaleString()} · viewed {s.views}×</span>
            <Button size="sm" variant="ghost" data-testid={`passport-revoke-${s.id}`} onClick={() => api.delete(`/portal/${slug}/shares/${s.id}`).then(load)}><Trash2 className="h-4 w-4 mr-1" />Stop sharing</Button>
          </div>))}
      </div>}
      <PassportBody p={p} photoUrl={(id) => `${process.env.REACT_APP_BACKEND_URL}/api/portal/${slug}/photos/${id}`} />
      <ShareDialog slug={slug} open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
}
